import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ThemeProvider } from '@mui/material/styles';
import { useCompanyContext } from '@/app/context/CompanyContext';
import { useUserContext } from '@/app/context/UserContext';
import { createAppTheme } from '@/config/mui-theme.config';
import { applyHtmlDarkClass, getStoredColorMode, isAuthMarketingDarkRoute, setStoredColorMode, shouldApplyDark } from '@/utils/color-mode.util';
const ColorModeContext = createContext(null);
export function ColorModeProvider({
  children,
  fontFamily
}) {
  const pathname = usePathname();
  const {
    hasNoUser,
    companyDetails
  } = useUserContext();
  const {
    companyConfig
  } = useCompanyContext();
  // Prefer user-data companyDetails (available on public routes via companySlug); fall back to CompanyContext.
  const isDarkModeFeatureEnabled = companyDetails?.isDarkModeEnabled === true || companyConfig?.isDarkModeEnabled === true;
  const allowDark = isAuthMarketingDarkRoute(pathname) || isDarkModeFeatureEnabled;
  const [colorMode, setColorModeState] = useState('light');
  const [preferenceReady, setPreferenceReady] = useState(false);
  useLayoutEffect(() => {
    setColorModeState(getStoredColorMode());
    setPreferenceReady(true);
  }, []);
  const isDark = preferenceReady && allowDark && shouldApplyDark({
    preference: colorMode,
    pathname,
    hasNoUser
  });
  useLayoutEffect(() => {
    applyHtmlDarkClass(isDark);
  }, [isDark]);
  const setColorMode = useCallback(mode => {
    setColorModeState(mode);
    setStoredColorMode(mode);
  }, []);
  const toggleColorMode = useCallback(() => {
    setColorMode(colorMode === 'dark' ? 'light' : 'dark');
  }, [colorMode, setColorMode]);

  // Recreate when createAppTheme identity changes (HMR / theme config edits).
  const theme = useMemo(() => createAppTheme(isDark ? 'dark' : 'light', fontFamily), [isDark, fontFamily, createAppTheme]);
  const value = useMemo(() => ({
    colorMode,
    setColorMode,
    toggleColorMode,
    isDark
  }), [colorMode, setColorMode, toggleColorMode, isDark]);
  return <ColorModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>;
}
export function useColorModeContext() {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used within ColorModeProvider');
  }
  return ctx;
}